/**
 * DOM Flow Builder
 * Builds React Flow nodes and edges from the DOM (#ListaAccionesOrden)
 * This is used for the embedded editor that syncs with the existing form editor
 */

import type { ActionNode, ActionEdge, ActionNodeData } from '../types';

// Node colors based on type (same as flowBuilder.ts)
const NODE_COLORS = {
  inicio: { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
  grupo: { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
  accion: { bg: '#f8fafc', border: '#e2e8f0', text: '#475569' },
  seccion: { bg: '#fce7f3', border: '#ec4899', text: '#9d174d' },
  checkpoint: { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
  posta: { bg: '#e0e7ff', border: '#6366f1', text: '#3730a3' },
  alternativa: { bg: '#fef9c3', border: '#eab308', text: '#854d0e' },
  fin: { bg: '#fef2f2', border: '#ef4444', text: '#991b1b' },
  ia: { bg: '#ecfdf5', border: '#10b981', text: '#065f46' },
  iaGenera: { bg: '#d1fae5', border: '#059669', text: '#047857' },
};

interface DOMAccionCheckboxes {
  hasNotification: boolean;
  hasDatabase: boolean;
  hasDatabaseCheckbox: boolean;
  isOptional: boolean;
  isInfinite: boolean;
  hasGalleryRestriction: boolean;
  isCompact: boolean;
  allEdit: boolean;
  titleHidden: boolean;
  groupTitle: boolean;
  groupTogether: boolean;
  hasImage: boolean;
  showIfComesFrom: boolean;
}

interface DOMAccionData {
  objectId: string;
  codigoUnico: string;
  tipo: string;
  texto: string;
  orden: number;
  ordenHijo?: number;
  padreId?: string;
  saltarA?: string | string[];
  finaliza: boolean;
  tieneIA: boolean;
  esSeccion: boolean;
  esGrupo: boolean;
  esPosta: boolean;
  esCheckpoint: boolean;
  esAlternativa: boolean;
  checkboxes: DOMAccionCheckboxes;
  postaGroups?: string[];
  conditionalDisplayGroups?: string[];
  alternativaOpciones?: Array<{
    texto: string;
    saltarA?: string;
    finaliza?: boolean;
  }>;
}

/**
 * Extract action data from a DOM <li> element
 */
function extractAccionFromLI(li: HTMLElement): DOMAccionData | null {
  // Skip empty actions and group closing tags
  // Use includes() to catch variations like 'GrupoDeAccionesTipeF-123'
  if (li.classList.contains('accionVacia') || li.id?.includes('GrupoDeAccionesTipeF')) {
    return null;
  }

  // Get CodigoUnico
  const codigoUnicoEl = li.querySelector('.CodigoUnico') as HTMLElement;
  if (!codigoUnicoEl || !codigoUnicoEl.id) {
    return null;
  }

  const codigoUnico = codigoUnicoEl.id;
  const itemId = li.id || '';

  // Get title
  const tituloEl = li.querySelector('.TituloAccion') as HTMLInputElement;
  let texto = tituloEl?.value || '';

  // For RequiereLocalizacion, get from geocomplete
  if (itemId.includes('RequiereLocalizacion')) {
    const geocompleteEl = li.querySelector('#geocomplete') as HTMLInputElement;
    if (geocompleteEl?.value) {
      texto = geocompleteEl.value;
    }
  }

  // Get order number
  const numeroOrdenEl = li.querySelector('.NumeroOrdenAccion') as HTMLElement;
  const orden = numeroOrdenEl ? parseInt(numeroOrdenEl.textContent || '0', 10) : 0;

  // Determine type
  const esSeccion = itemId.includes('Seccion');
  const esGrupo = itemId.includes('GrupoDeAcciones');
  const esPosta = itemId.includes('Posta');
  const esCheckpoint = itemId.includes('Checkpoint') ||
                       itemId.includes('ApruebaExterno') ||
                       itemId.includes('SolicitudDeArchivo') ||
                       itemId.includes('RequiereLocalizacion');
  const esAlternativa = itemId.includes('Alternativa');

  // Check if nested (child of group)
  const esHijo = li.classList.contains('EsDeGrupoDeAcciones');
  let padreId: string | undefined;
  if (esHijo) {
    // Find parent by looking at previous siblings
    let prevSibling = li.previousElementSibling as HTMLElement;
    while (prevSibling) {
      if (prevSibling.id?.includes('GrupoDeAcciones') &&
          !prevSibling.id?.includes('TipeF')) {
        const padreCodigoEl = prevSibling.querySelector('.CodigoUnico') as HTMLElement;
        if (padreCodigoEl) {
          padreId = padreCodigoEl.id;
          break;
        }
      }
      prevSibling = prevSibling.previousElementSibling as HTMLElement;
    }
  }

  // Extract flow control (SaltarA / Finalizar)
  let saltarA: string | string[] | undefined;
  let finaliza = false;

  // Find selected flow option (O1=next, O2=jump, O3=finish)
  // Only look at main action flow options, not alternative options
  const selectedOptions = li.querySelectorAll('.celdaOpcionAccionSelected');
  const mainSelectedOption = Array.from(selectedOptions).find(
    opt => !opt.closest('.InputsAlternativasTexto2.existeAlternativa')
  );

  if (mainSelectedOption) {
    const optionParent = mainSelectedOption.closest('a');
    if (optionParent?.id === 'O3') {
      finaliza = true;
    } else if (optionParent?.id === 'O2') {
      // Find jump target
      const jumpContainers = li.querySelectorAll('.OpcionesSaltarAContenedor .OpcionSeleccionada');
      const mainJumpContainer = Array.from(jumpContainers).find(
        c => !c.closest('.InputsAlternativasTexto2.existeAlternativa')
      );
      if (mainJumpContainer) {
        const jumpLink = mainJumpContainer.querySelector('a.OpcionesSaltarA') as HTMLElement;
        if (jumpLink?.id && !jumpLink.id.startsWith('_')) {
          saltarA = jumpLink.id;
        }
      }
    }
  }

  // Check IA
  const iaCheckbox = li.querySelector('.ia-accion-activo') as HTMLInputElement;
  const tieneIA = iaCheckbox?.checked || false;

  // Extract all checkbox states - MUST match exact class names from sortable-blocks-init.js
  const databaseCheckbox = li.querySelector('.HabilitarGuardarBaseDeDatos') as HTMLInputElement;
  const checkboxes: DOMAccionCheckboxes = {
    hasNotification: (li.querySelector('.HabilitarNotificar') as HTMLInputElement)?.checked || false,
    hasDatabase: databaseCheckbox?.checked ?? true, // Default true if checkbox doesn't exist
    hasDatabaseCheckbox: !!databaseCheckbox,
    isOptional: (li.querySelector('.HabilitarAccionOpcional') as HTMLInputElement)?.checked || false,
    isInfinite: (li.querySelector('.HabilitarAccionInfinita') as HTMLInputElement)?.checked || false,
    hasGalleryRestriction: (li.querySelector('.HabilitarRestringirGaleria') as HTMLInputElement)?.checked || false,
    isCompact: (li.querySelector('.HabilitarCompacta') as HTMLInputElement)?.checked || false,
    allEdit: (li.querySelector('.HabilitarTodosEditan') as HTMLInputElement)?.checked || false,
    titleHidden: (li.querySelector('.BoolTituloNoVisible') as HTMLInputElement)?.checked || false,
    groupTitle: (li.querySelector('#BoolTituloGrupo') as HTMLInputElement)?.checked || false,
    groupTogether: (li.querySelector('#BoolTituloGrupoJuntos') as HTMLInputElement)?.checked || false,
    hasImage: (li.querySelector('#BoolImagenAccion') as HTMLInputElement)?.checked || false, // ID selector!
    showIfComesFrom: (li.querySelector('.HabilitarMostrarSiVieneDe') as HTMLInputElement)?.checked || false,
  };

  // Extract Posta group transfers
  let postaGroups: string[] | undefined;
  if (esPosta) {
    const notificarGruposTextarea = li.querySelector('textarea.NotificarGrupos') as HTMLTextAreaElement;
    if (notificarGruposTextarea?.value) {
      try {
        const tags = JSON.parse(notificarGruposTextarea.value);
        if (Array.isArray(tags)) {
          postaGroups = tags.map((t: { value?: string }) => t.value || '').filter(Boolean);
        }
      } catch {
        // Try to get from tagify container
        const tagifyContainer = li.querySelector('.tagify.NotificarGrupos');
        if (tagifyContainer) {
          const tagTexts = tagifyContainer.querySelectorAll('.tagify__tag-text');
          postaGroups = Array.from(tagTexts).map(el => el.textContent || '').filter(Boolean);
        }
      }
    }
  }

  // Extract conditional display groups
  let conditionalDisplayGroups: string[] | undefined;
  if (checkboxes.showIfComesFrom) {
    const gruposTextarea = li.querySelector('textarea.gruposMostrarSiVieneDe') as HTMLTextAreaElement;
    if (gruposTextarea?.value) {
      try {
        const tags = JSON.parse(gruposTextarea.value);
        if (Array.isArray(tags)) {
          conditionalDisplayGroups = tags.map((t: { value?: string }) => t.value || '').filter(Boolean);
        }
      } catch {
        const tagifyContainer = li.querySelector('.tagify.gruposMostrarSiVieneDe');
        if (tagifyContainer) {
          const tagTexts = tagifyContainer.querySelectorAll('.tagify__tag-text');
          conditionalDisplayGroups = Array.from(tagTexts).map(el => el.textContent || '').filter(Boolean);
        }
      }
    }
  }

  // Extract alternative options with their own SaltarA
  let alternativaOpciones: DOMAccionData['alternativaOpciones'];
  if (esAlternativa) {
    alternativaOpciones = [];
    const altDivs = li.querySelectorAll('.InputsAlternativasTexto2.existeAlternativa');
    altDivs.forEach(altDiv => {
      const altInput = altDiv.querySelector('.TituloAlternativa') as HTMLInputElement;
      const altTexto = altInput?.value || '';

      // Check for SaltarA in this alternative
      let altSaltarA: string | undefined;
      let altFinaliza = false;

      const altSelectedOpt = altDiv.querySelector('.celdaOpcionAccionSelected');
      if (altSelectedOpt) {
        const altOptParent = altSelectedOpt.closest('a');
        if (altOptParent?.id === 'O3') {
          altFinaliza = true;
        } else if (altOptParent?.id === 'O2') {
          const altJumpContainer = altDiv.querySelector('.OpcionesSaltarAContenedor .OpcionSeleccionada');
          if (altJumpContainer) {
            const altJumpLink = altJumpContainer.querySelector('a.OpcionesSaltarA') as HTMLElement;
            if (altJumpLink?.id && !altJumpLink.id.startsWith('_')) {
              altSaltarA = altJumpLink.id;
            }
          }
        }
      }

      alternativaOpciones!.push({
        texto: altTexto,
        saltarA: altSaltarA,
        finaliza: altFinaliza,
      });
    });
  }

  return {
    objectId: codigoUnico, // Use codigoUnico as objectId for consistency
    codigoUnico,
    tipo: itemId,
    texto: texto || `Acción ${orden}`,
    orden,
    padreId,
    saltarA,
    finaliza,
    tieneIA,
    esSeccion,
    esGrupo,
    esPosta,
    esCheckpoint,
    esAlternativa,
    checkboxes,
    postaGroups,
    conditionalDisplayGroups,
    alternativaOpciones,
  };
}

/**
 * Build React Flow nodes and edges from DOM
 */
export function buildFlowFromDOM(
  selectedCodigoUnico?: string | null
): { nodes: ActionNode[]; edges: ActionEdge[] } {
  const nodes: ActionNode[] = [];
  const edges: ActionEdge[] = [];

  // Get ListaAccionesOrden from DOM
  const listaAcciones = document.getElementById('ListaAccionesOrden');
  if (!listaAcciones) {
    console.warn('[domFlowBuilder] #ListaAccionesOrden not found');
    return { nodes, edges };
  }

  // Extract all actions from DOM
  const acciones: DOMAccionData[] = [];
  const seenCodigoUnicos = new Set<string>();
  const liElements = listaAcciones.querySelectorAll('li.list-group-item');

  liElements.forEach(li => {
    const accion = extractAccionFromLI(li as HTMLElement);
    if (accion) {
      // Dedupe by codigoUnico (prevent duplicate entries for group start/end)
      if (!seenCodigoUnicos.has(accion.codigoUnico)) {
        seenCodigoUnicos.add(accion.codigoUnico);
        acciones.push(accion);
      }
    }
  });

  // Debug log
  console.log('[domFlowBuilder] Extracted actions:', acciones.map(a => ({
    i: acciones.indexOf(a),
    codigo: a.codigoUnico,
    numero: a.orden,
    titulo: a.texto,
    tipo: a.tipo.replace(/Tipe.*/, 'Tipe'),
    saltarA: a.saltarA || null,
    finaliza: a.finaliza,
    padreId: a.padreId || null,
  })));

  if (acciones.length === 0) {
    console.log('[domFlowBuilder] No actions found, returning empty');
    return { nodes, edges };
  }

  console.log('[domFlowBuilder] Processing', acciones.length, 'actions...');

  // Create maps for lookup
  const accionMap = new Map<string, DOMAccionData>();
  acciones.forEach(a => {
    accionMap.set(a.codigoUnico, a);
  });

  // Separate root actions from children
  const rootAcciones = acciones.filter(a => !a.padreId);
  const childrenMap = new Map<string, DOMAccionData[]>();

  acciones.forEach(accion => {
    if (accion.padreId) {
      if (!childrenMap.has(accion.padreId)) {
        childrenMap.set(accion.padreId, []);
      }
      childrenMap.get(accion.padreId)!.push(accion);
    }
  });

  // Sort by order
  rootAcciones.sort((a, b) => a.orden - b.orden);
  childrenMap.forEach(children => {
    children.sort((a, b) => a.orden - b.orden);
  });

  // Note: Orphan detection removed - all actions shown as part of flow
  // Jump edges (animated) already indicate the actual execution path
  // INICIO and FIN nodes removed - user doesn't want to see them

  let nodeIndex = 1;

  // Process each action
  function processAction(accion: DOMAccionData, parentId?: string): void {
    const nodeId = accion.codigoUnico;

    // Determine node type
    let tipo: ActionNodeData['tipo'] = 'accion';
    if (accion.esSeccion) tipo = 'seccion';
    else if (accion.esGrupo) tipo = 'grupo';
    else if (accion.esPosta) tipo = 'posta';
    else if (accion.esCheckpoint) tipo = 'checkpoint';
    else if (accion.esAlternativa) tipo = 'alternativa';

    // Get colors
    let colors = NODE_COLORS[tipo] || NODE_COLORS.accion;
    if (accion.tieneIA) {
      colors = NODE_COLORS.ia;
    }

    // Check if selected
    const isSelected = selectedCodigoUnico === nodeId;

    // Get children for groups
    const children = childrenMap.get(accion.codigoUnico) || [];
    const childrenData = children.map((child, idx) => ({
      codigoUnico: child.codigoUnico,
      label: child.texto || `Acción ${idx + 1}`,
      orden: child.orden,
    }));

    // Determine React Flow node type
    const nodeType = accion.esGrupo ? 'groupNode' : 'actionNode';

    // Create node
    const node: ActionNode = {
      id: nodeId,
      type: nodeType,
      position: { x: 0, y: 0 },
      data: {
        label: accion.texto,
        codigoUnico: nodeId,
        accionId: accion.objectId,
        tipo,
        orden: nodeIndex++,
        tipoAccion: accion.tipo,
        tieneIA: accion.tieneIA,
        esSeccion: accion.esSeccion,
        esPosta: accion.esPosta,
        esCheckpoint: accion.esCheckpoint,
        esAlternativa: accion.esAlternativa,
        saltaA: accion.saltarA,
        tieneSiguiente: !accion.finaliza,
        finaliza: accion.finaliza,
        padreId: parentId,
        isSelected,
        checkboxes: accion.checkboxes,
        postaGroups: accion.postaGroups,
        conditionalDisplayGroups: accion.conditionalDisplayGroups,
        // Include alternative options for display and edges
        alternativaOpciones: accion.alternativaOpciones,
        // Include children for groups
        children: accion.esGrupo ? childrenData : undefined,
      },
      style: {
        backgroundColor: colors.bg,
        borderColor: isSelected ? '#10b981' : colors.border,
        color: colors.text,
        borderWidth: isSelected ? 3 : (accion.tieneIA ? 3 : 2),
        boxShadow: isSelected ? '0 0 0 3px rgba(16, 185, 129, 0.3)' : undefined,
      },
    };

    nodes.push(node);

    // Process children (use existing children variable from line 395)
    children.forEach(child => processAction(child, nodeId));
  }

  // Process all root actions
  rootAcciones.forEach(accion => processAction(accion));

  // FIN node removed - user doesn't want to see it

  // Build node lookup map
  const nodeMap = new Map<string, ActionNode>();
  nodes.forEach(node => {
    nodeMap.set(node.id, node);
    if (node.data.codigoUnico) {
      nodeMap.set(node.data.codigoUnico, node);
    }
  });

  // Create edges - SIMPLE LOGIC: connect all actions sequentially

  // Helper: resolve jump target to node ID
  function resolveJumpTarget(saltarA: string | undefined): string | null {
    if (!saltarA) return null;
    const targetStr = String(saltarA);
    if (nodeMap.has(targetStr)) return targetStr;
    // Try base part for format "codigoUnico-X-Y"
    if (targetStr.includes('-')) {
      const basePart = targetStr.split('-')[0];
      if (nodeMap.has(basePart)) return basePart;
    }
    return null;
  }

  // 1. Parent-child edges (for group children)
  acciones.forEach(accion => {
    if (accion.padreId) {
      edges.push({
        id: `edge-child-${accion.padreId}-${accion.codigoUnico}`,
        source: accion.padreId,
        target: accion.codigoUnico,
        type: 'default',
        animated: false,
        style: { stroke: '#cbd5e1' },
        data: { tipo: 'siguiente' },
      });
    }
  });

  // 2. Build transition graph per root — every possible execution path
  // Each root has outgoing transitions based on: saltarA, alternativa options, or sequential next
  type Transition = { target: string; label?: string; kind: 'seq' | 'jump' | 'alt'; optIdx?: number; fullText?: string };
  const transitions = new Map<string, Transition[]>();
  const rootIdx = new Map<string, number>();
  rootAcciones.forEach((a, i) => rootIdx.set(a.codigoUnico, i));

  rootAcciones.forEach((accion, idx) => {
    const outs: Transition[] = [];
    const next = rootAcciones[idx + 1];

    if (accion.finaliza) {
      // flow ends here, no outgoing
    } else if (accion.alternativaOpciones && accion.alternativaOpciones.length > 0) {
      // Each alternativa option is a branch
      accion.alternativaOpciones.forEach((opt, optIdx) => {
        const letter = String.fromCharCode(65 + optIdx);
        if (opt.saltarA) {
          const t = resolveJumpTarget(opt.saltarA);
          if (t) outs.push({ target: t, label: letter, kind: 'alt', optIdx, fullText: opt.texto });
        } else if (!opt.finaliza && next) {
          outs.push({ target: next.codigoUnico, label: letter, kind: 'alt', optIdx, fullText: opt.texto });
        }
      });
    } else if (accion.saltarA) {
      const targets = Array.isArray(accion.saltarA) ? accion.saltarA : [accion.saltarA];
      targets.forEach(t => {
        const resolved = resolveJumpTarget(String(t));
        if (resolved) outs.push({ target: resolved, kind: 'jump' });
      });
    } else if (next) {
      outs.push({ target: next.codigoUnico, kind: 'seq' });
    }

    transitions.set(accion.codigoUnico, outs);
  });

  // 3. Compute levels via longest-path BFS from true starts (no incoming edges)
  const incomingCount = new Map<string, number>();
  rootAcciones.forEach(a => incomingCount.set(a.codigoUnico, 0));
  transitions.forEach(outs => {
    outs.forEach(t => incomingCount.set(t.target, (incomingCount.get(t.target) || 0) + 1));
  });

  const starts = rootAcciones.filter(a => (incomingCount.get(a.codigoUnico) || 0) === 0);
  const levels = new Map<string, number>();
  // Initialize all as unvisited
  const computeLevel = (id: string, depth: number, visiting: Set<string>) => {
    if (visiting.has(id)) return; // cycle guard
    const current = levels.get(id);
    if (current !== undefined && current >= depth) return;
    levels.set(id, depth);
    visiting.add(id);
    const outs = transitions.get(id) || [];
    outs.forEach(t => computeLevel(t.target, depth + 1, visiting));
    visiting.delete(id);
  };
  starts.forEach(s => computeLevel(s.codigoUnico, 0, new Set()));
  // Fallback for any unreachable node
  rootAcciones.forEach(a => {
    if (!levels.has(a.codigoUnico)) levels.set(a.codigoUnico, 0);
  });

  // 4. Emit edges from transitions
  transitions.forEach((outs, sourceId) => {
    outs.forEach((t, i) => {
      const base = {
        id: `edge-${t.kind}-${sourceId}-${t.target}-${i}`,
        source: sourceId,
        target: t.target,
      };
      if (t.kind === 'seq') {
        edges.push({
          ...base,
          type: 'addButton',
          animated: false,
          style: { stroke: '#94a3b8' },
          data: { tipo: 'siguiente' },
        });
      } else if (t.kind === 'jump') {
        edges.push({
          ...base,
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#f59e0b', strokeWidth: 2 },
          label: '→',
          labelStyle: { fill: '#fff', fontSize: 10, fontWeight: 600 },
          labelBgStyle: { fill: '#f59e0b', fillOpacity: 1 },
          labelBgPadding: [4, 4] as [number, number],
          labelBgBorderRadius: 10,
          data: { tipo: 'salto', label: 'Salta' },
        });
      } else {
        // alt
        edges.push({
          ...base,
          sourceHandle: t.optIdx !== undefined ? `alt-${t.optIdx}` : undefined,
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#8b5cf6', strokeWidth: 2 },
          label: t.label,
          labelStyle: { fill: '#fff', fontSize: 9, fontWeight: 600 },
          labelBgStyle: { fill: '#8b5cf6', fillOpacity: 1 },
          labelBgPadding: [4, 3] as [number, number],
          labelBgBorderRadius: 3,
          data: { tipo: 'alternativa', label: t.label, fullText: t.fullText },
        });
      }
    });
  });

  // 5. Assign flowOrder from levels. Group children inherit parent's level
  nodes.forEach(node => {
    const own = levels.get(node.id);
    if (own !== undefined) {
      node.data.flowOrder = own;
    } else if (node.data.padreId) {
      node.data.flowOrder = levels.get(node.data.padreId) ?? 0;
    } else {
      node.data.flowOrder = 0;
    }
  });

  console.log('[buildFlowFromDOM] Levels:', Array.from(levels.entries()));

  return { nodes, edges };
}

/**
 * Apply simple layout to nodes - sequential layout by flowOrder
 * Nodes at the same flowOrder stack perpendicular to the flow axis
 */
export function applySimpleLayout(
  nodes: ActionNode[],
  _edges: ActionEdge[],
  orientation: 'LR' | 'TB' = 'LR'
): ActionNode[] {
  const nodeSpacing = orientation === 'LR' ? { x: 280, y: 120 } : { x: 200, y: 140 };
  const startPos = { x: 50, y: 50 };
  const LR_CENTER_Y = 250; // common vertical center in LR mode

  // Estimate node height to center-align in LR mode
  const estimatedHeight = (n: ActionNode): number => {
    if (n.data.tipo === 'grupo') return 80;
    const altCount = (n.data as { alternativaOpciones?: unknown[] }).alternativaOpciones?.length ?? 0;
    if (altCount > 0) return 90 + altCount * 26;
    return 70;
  };

  // LR: override flowOrder so every node is its own level (linear horizontal chain)
  // TB: keep hierarchy flowOrder (parent row, children stacked horizontally in next row)
  if (orientation === 'LR') {
    nodes.forEach((n, i) => { n.data.flowOrder = i; });
  }

  const nodesByLevel = new Map<number, ActionNode[]>();
  nodes.forEach(node => {
    const level = node.data.flowOrder ?? 0;
    if (!nodesByLevel.has(level)) nodesByLevel.set(level, []);
    nodesByLevel.get(level)!.push(node);
  });

  console.log('[applySimpleLayout] Nodes by level:', Object.fromEntries(
    Array.from(nodesByLevel.entries()).map(([k, v]) => [k, v.map(n => n.id)])
  ));

  const positionedNodes: ActionNode[] = [];
  const MAIN_CENTER_X = 500;

  nodes.forEach(node => {
    const level = node.data.flowOrder ?? 0;
    const nodesAtLevel = nodesByLevel.get(level) || [];
    const indexAtLevel = nodesAtLevel.indexOf(node);
    const totalAtLevel = nodesAtLevel.length;

    let x: number, y: number;
    if (orientation === 'LR') {
      x = startPos.x + level * nodeSpacing.x;
      y = LR_CENTER_Y - estimatedHeight(node) / 2;
    } else {
      // TB: nodes at same level stack side-by-side centered around MAIN_CENTER_X
      x = MAIN_CENTER_X + (indexAtLevel - (totalAtLevel - 1) / 2) * nodeSpacing.x;
      y = startPos.y + level * nodeSpacing.y;
    }

    positionedNodes.push({
      ...node,
      position: { x, y },
      data: { ...node.data, orientation },
    });
  });

  return positionedNodes;
}
